import { useEffect } from "react";
import { ControlBar, ControlBarButton, Group, Icon, Separator } from "@salestrak/ui";

import { isTextEditingTarget } from "#app/lib/utils/typing-target";

export type CanvasDensity = "full" | "compact" | "overview";

const CANVAS_ZOOM_LEVELS = [
  { density: "full", label: "Normal" },
  { density: "compact", label: "Compact" },
  { density: "overview", label: "Overview" },
] as const satisfies readonly { density: CanvasDensity; label: string }[];

function levelLabel(density: CanvasDensity): string {
  return CANVAS_ZOOM_LEVELS.find((level) => level.density === density)?.label ?? "";
}

function stepDensity(density: CanvasDensity, steps: number): CanvasDensity {
  const index = CANVAS_ZOOM_LEVELS.findIndex((level) => level.density === density);
  return CANVAS_ZOOM_LEVELS[index + steps]?.density ?? density;
}

export function CanvasZoomControls({
  density,
  onChange,
}: {
  density: CanvasDensity;
  onChange: (density: CanvasDensity) => void;
}) {
  const out = stepDensity(density, 1);
  const into = stepDensity(density, -1);
  return (
    <ControlBar>
      <Group orientation="horizontal">
        <ControlBarButton
          disabled={out === density}
          label="Zoom out"
          onClick={() => onChange(out)}
          shortcut={["-"]}
          size="icon-xs"
          tooltip={`Zoom out to ${levelLabel(out)}`}
        >
          <Icon name="minus" size="xs" />
        </ControlBarButton>
        <Separator orientation="vertical" />
        <ControlBarButton
          disabled={into === density}
          label="Zoom in"
          onClick={() => onChange(into)}
          shortcut={["+"]}
          size="icon-xs"
          tooltip={`Zoom in to ${levelLabel(into)}`}
        >
          <Icon name="plus" size="xs" />
        </ControlBarButton>
      </Group>
    </ControlBar>
  );
}

/** Ignored while a text field has focus or a modifier is held. */
export function useCanvasZoomHotkeys({
  enabled,
  density,
  onChange,
}: {
  enabled: boolean;
  density: CanvasDensity;
  onChange: (density: CanvasDensity) => void;
}) {
  useEffect(() => {
    if (!enabled) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTextEditingTarget(document.activeElement)) return;
      const next =
        event.key === "-" || event.key === "_"
          ? stepDensity(density, 1)
          : event.key === "+" || event.key === "="
            ? stepDensity(density, -1)
            : event.key === "0"
              ? "full"
              : undefined;
      if (next === undefined) return;
      event.preventDefault();
      if (next !== density) onChange(next);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [enabled, density, onChange]);
}

export const CANVAS_ITEM_ATTRIBUTE = "data-canvas-item";

export type CanvasAnchor = {
  itemId: string;
  along: number;
  offset: number;
};

/** Held still across a level change so the cards redraw around it instead of the view jumping. */
export function readCanvasAnchor(
  scroller: HTMLElement,
  selectedItemId: string | null,
): CanvasAnchor | null {
  const view = scroller.getBoundingClientRect();
  const middle = view.top + view.height / 2;
  let best: { itemId: string; box: DOMRect; distance: number } | null = null;
  for (const card of scroller.querySelectorAll<HTMLElement>(`[${CANVAS_ITEM_ATTRIBUTE}]`)) {
    const itemId = card.getAttribute(CANVAS_ITEM_ATTRIBUTE)!;
    const box = card.getBoundingClientRect();
    if (itemId === selectedItemId && box.bottom > view.top && box.top < view.bottom) {
      return { along: 0, itemId, offset: box.top - view.top };
    }
    const distance = box.top > middle ? box.top - middle : box.bottom < middle ? middle - box.bottom : 0;
    if (!best || distance < best.distance) best = { box, distance, itemId };
  }
  if (!best) return null;
  const along = best.box.height > 0 ? Math.min(1, Math.max(0, (middle - best.box.top) / best.box.height)) : 0;
  return { along, itemId: best.itemId, offset: best.box.top + along * best.box.height - view.top };
}

export function revealCanvasItem(
  scroller: HTMLElement,
  itemId: string,
  { margin, smooth }: { margin: number; smooth: boolean },
): void {
  const card = scroller.querySelector<HTMLElement>(
    `[${CANVAS_ITEM_ATTRIBUTE}="${CSS.escape(itemId)}"]`,
  );
  if (!card) return;
  const view = scroller.getBoundingClientRect();
  const box = card.getBoundingClientRect();
  const above = box.top - (view.top + margin);
  const below = box.bottom - (view.bottom - margin);
  const delta = above < 0 ? above : below > 0 ? Math.min(above, below) : 0;
  if (delta !== 0) scroller.scrollBy({ behavior: smooth ? "smooth" : "auto", top: delta });
}

export function restoreCanvasAnchor(scroller: HTMLElement, anchor: CanvasAnchor): void {
  const card = scroller.querySelector<HTMLElement>(
    `[${CANVAS_ITEM_ATTRIBUTE}="${CSS.escape(anchor.itemId)}"]`,
  );
  if (!card) return;
  const box = card.getBoundingClientRect();
  const point = box.top + anchor.along * box.height - scroller.getBoundingClientRect().top;
  scroller.scrollTop += point - anchor.offset;
}
